import { notifications } from "@mantine/notifications"
import {
  UseMutationOptions,
  useMutation,
  useQuery,
} from "@tanstack/react-query"

import { AdminResponses } from "@tfg/types/endpoints/admin"
import {
  standardsCreateValidationType,
  standardsUpdateValidationType,
} from "@tfg/types/schemas/admin"

import { httpClient } from "~/backend/axios"
import { queryClient } from "~/backend/queryClient"

export const useStandardsQuery = () => {
  return useQuery(["standards"], async () => {
    const res =
      await httpClient.get<AdminResponses.Standards[]>("/admin/standards")
    return res.data
  })
}

export const useDeleteStandardMutation = (
  options?: UseMutationOptions<void, unknown, number>
) => {
  return useMutation({
    ...options,
    mutationFn: async (id: number) => {
      const res = await httpClient.delete(`/admin/standards/${id}`)
      return res.data
    },
    onSuccess: (data, variables, context) => {
      options?.onSuccess?.(data, variables, context)
      queryClient.invalidateQueries(["standards"])
      notifications.show({
        title: "Deleted",
        message: "Successfully deleted standard",
        color: "green",
      })
    },
  })
}

export const useCreateStandardMutation = (
  options?: UseMutationOptions<
    standardsCreateValidationType,
    unknown,
    standardsCreateValidationType
  >
) => {
  return useMutation<
    standardsCreateValidationType,
    unknown,
    standardsCreateValidationType
  >({
    ...options,
    mutationFn: async (data: standardsCreateValidationType) => {
      const res = await httpClient.post<standardsCreateValidationType>(
        "/admin/standards",
        { ...data }
      )
      return res.data
    },
    onSuccess: (data, variables, context) => {
      options?.onSuccess?.(data, variables, context)
      queryClient.invalidateQueries(["standards"])
      notifications.show({
        title: "Added",
        message: "Successfully added standard",
        color: "green",
      })
    },
  })
}

export const useUpdateStandardMutation = (
  options?: UseMutationOptions<
    standardsUpdateValidationType,
    unknown,
    standardsUpdateValidationType
  >
) =>
  useMutation<
    standardsUpdateValidationType,
    unknown,
    standardsUpdateValidationType
  >({
    mutationFn: async (standard: standardsUpdateValidationType) => {
      const res = await httpClient.patch<standardsUpdateValidationType>(
        `/admin/standards`,
        standard
      )
      return res.data
    },
    onSuccess: (data, variables, context) => {
      options?.onSuccess?.(data, variables, context)
      queryClient.invalidateQueries(["standards"])
      notifications.show({
        title: "Added",
        message: "Successfully updated certificate Type",
        color: "green",
      })
    },
  })

export const useListCertificateTypesQuery = () => {
  return useQuery(["certificateTypesList"], async () => {
    const res = await httpClient.get<AdminResponses.CertificateTypeList[]>(
      "/admin/certificate-types-list"
    )
    return res.data
  })
}
